import React, { useCallback } from 'react';
import { EVENTS, Lang, COLLABORATOR_PERMISSIONS_GROUP, USER_PERMISSIONS } from 'shared/constants';
import { Alert, notification } from 'antd';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { INTERNAL_ROUTING, ROUTES } from 'shared/constants/routes';
import { canBulkUploadAvatars } from 'shared/store/selectors/iam.selector';
import { DeleteUserConfirm } from './delete-user-confirm';
import { TransferOwner } from './transfer-owner';
import { BulkAvatarUpload } from './bulk-avatar-upload';
import { UserList } from 'src/components/user-list';
import { ENTITY, useColumns } from 'src/components/user-list/use-columns.hooks';
import analyticsService from 'src/helpers/analytics.service';
import { formatNumber } from 'shared/utils';
import { useProfileFieldOptionAffectedUsers } from 'shared/hooks/profile-fields.hooks';

const selectAffectedEmails = ({ users }) =>
  users.map(user => user.email?.toLowerCase()).filter(Boolean);

const UserManagementPageComponent = React.memo(function UserManagementPageComponent({
  lang = Lang.USERS_MANAGEMENT,
  users = null,
  deleteUser: deleteUserAction,
  bulkDeleteUsers: bulkDeleteUsersAction,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const affectedFieldId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_FIELD);
  const affectedOptionId = searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_OPTION);
  const affectedScope =
    searchParams.get(INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_SCOPE) ?? undefined;
  // Opened from the profile-field delete modal: pre-filter to the affected users. No focus
  // refetch, so a filter the admin edits isn't overwritten.
  const isAffectedFilter = !!affectedFieldId && !!affectedOptionId;
  const affectedQuery = useProfileFieldOptionAffectedUsers(
    { fieldId: affectedFieldId, optionId: affectedOptionId, scope: affectedScope },
    { enabled: isAffectedFilter, select: selectAffectedEmails, refetchOnWindowFocus: false },
  );
  const affectedEmails = affectedQuery.data;
  // An empty email filter means "no filter", so until the list resolves to some emails,
  // never fall back to every user: show a loader, then an empty list with a notice.
  const affectedNotice =
    isAffectedFilter && affectedQuery.isError
      ? { type: 'error', message: lang.AFFECTED_USERS_LOAD_ERROR }
      : isAffectedFilter && affectedEmails?.length === 0
        ? { type: 'info', message: lang.AFFECTED_USERS_NONE }
        : null;
  const getListUsers = () => {
    if (!isAffectedFilter) return users;
    if (affectedQuery.isPending) return null;
    if (affectedNotice) return [];
    return users;
  };
  const [deleteUserState, setDeleteUserState] = React.useState(null);
  const [bulkDeleteUsers, setBulkDeleteUsers] = React.useState(null);
  const [transferOwnership, setTransferOwnership] = React.useState(null);
  const [api, contextHolder] = notification.useNotification();
  const clearSelectionRef = React.useRef(null);
  const canBulkUpload = useSelector(canBulkUploadAvatars);

  const openUserDetails = useCallback(
    user => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set(INTERNAL_ROUTING.QUERY_PARAMS.USERS, user.id);
        return newParams;
      });
    },
    [setSearchParams],
  );

  const columns = useColumns({
    entity: ENTITY.USER,
    showLastLogin: true,
    onEditUser: openUserDetails,
  });

  const clearAffectedFilter = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_FIELD);
      newParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_OPTION);
      newParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.USERS_AFFECTED_SCOPE);
      return newParams;
    });
  }, [setSearchParams]);

  const closeDetails = useCallback(() => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete(INTERNAL_ROUTING.QUERY_PARAMS.USERS);
      return newParams;
    });
  }, [setSearchParams]);

  const openNotification = useCallback(
    ({ message, type = 'success' }) => {
      api[type]({
        title: message,
        placement: 'bottomRight',
        duration: 10,
      });
    },
    [api],
  );

  const onDeleteUser = useCallback(
    (user, onFinish) => {
      setDeleteUserState({
        user,
        action: async ({ ownerId }) => {
          const res = await deleteUserAction({ id: user.id, ownerId });

          if (!res?.error) {
            openNotification({ message: lang.NOTIFICATION_DELETE_SUCCESS });
            setDeleteUserState(null);
            if (onFinish) {
              onFinish();
            }
          }

          return res;
        },
        dismiss: () => {
          setDeleteUserState(null);
        },
      });
    },
    [deleteUserAction, openNotification, lang],
  );

  // Bulk delete
  const onBulkDelete = useCallback(
    selectedUsers => {
      // Check if any of the selected users has COLLABORATOR or higher permissions
      const hasCollaboratorOrAbove = selectedUsers.some(user =>
        COLLABORATOR_PERMISSIONS_GROUP.includes(user.permissions),
      );

      if (hasCollaboratorOrAbove) {
        // Trigger ownership transfer flow
        setTransferOwnership({
          users: selectedUsers,
          action: async ({ ownerId }) => {
            const userIds = selectedUsers.map(u => u.id);
            const res = await bulkDeleteUsersAction({ userIds, ownerId });

            if (!res?.error) {
              openNotification({
                message: lang.NOTIFICATION_BULK_DELETE_SUCCESS || 'Users deleted successfully',
              });
              setBulkDeleteUsers(null);
              setTransferOwnership(null);
              if (clearSelectionRef.current) {
                clearSelectionRef.current();
              }
              closeDetails();
            }
          },
          dismiss: () => {
            setTransferOwnership(null);
          },
        });
      } else {
        // No ownership transfer needed, show confirmation
        setBulkDeleteUsers(selectedUsers);
      }
    },
    [bulkDeleteUsersAction, openNotification, lang, closeDetails],
  );

  const confirmBulkDelete = useCallback(async () => {
    const userIds = bulkDeleteUsers.map(u => u.id);
    const res = await bulkDeleteUsersAction({ userIds });

    if (!res?.error) {
      openNotification({
        message: lang.NOTIFICATION_BULK_DELETE_SUCCESS || 'Users deleted successfully',
      });
      setBulkDeleteUsers(null);
      if (clearSelectionRef.current) {
        clearSelectionRef.current();
      }
      closeDetails();
    }
  }, [bulkDeleteUsers, bulkDeleteUsersAction, openNotification, lang, closeDetails]);

  const cancelBulkDelete = useCallback(() => setBulkDeleteUsers(null), []);

  // Transfer ownership
  const cancelTransferOwnership = useCallback(() => setTransferOwnership(null), []);

  const onTransferOwnership = useCallback(
    ({ user, action, dismiss }) => {
      setTransferOwnership({
        user,
        action,
        dismiss: () => {
          dismiss();
          cancelTransferOwnership();
        },
      });
    },
    [cancelTransferOwnership],
  );

  const onAddUser = () => {
    analyticsService.track(EVENTS.USER_ADD.ADD_CLICK);
  };

  const handleClearSelection = React.useCallback(clearFn => {
    clearSelectionRef.current = clearFn;
  }, []);
  return (
    <>
      {affectedNotice && (
        <Alert
          type={affectedNotice.type}
          showIcon
          message={
            <>
              {affectedNotice.message}{' '}
              <Link to={ROUTES.USERS_MANAGEMENT}>{lang.AFFECTED_USERS_SHOW_ALL}</Link>
            </>
          }
        />
      )}
      <UserList
        users={getListUsers()}
        columns={columns}
        pageTitle={lang.HEADER}
        buttonText={lang.ADD_NEW_USER}
        onDelete={onDeleteUser}
        onBulkDelete={onBulkDelete}
        onTransferOwnership={onTransferOwnership}
        openNotification={openNotification}
        notificationContextHolder={contextHolder}
        baseRoute={ROUTES.USERS_MANAGEMENT}
        onAdd={onAddUser}
        bulkUploadButton={canBulkUpload ? <BulkAvatarUpload /> : null}
        detailsRouteQueryParam={INTERNAL_ROUTING.QUERY_PARAMS.USERS}
        multiSelect
        clearSelection={handleClearSelection}
        showEmailListFilter
        presetEmailFilter={affectedEmails}
        onEmailFilterClear={clearAffectedFilter}
      />
      <DeleteUserConfirm
        open={!!deleteUserState}
        user={deleteUserState?.user}
        onConfirm={deleteUserState?.action}
        onDismiss={deleteUserState?.dismiss}
      />

      <DeleteUserConfirm
        open={!!bulkDeleteUsers}
        user={
          bulkDeleteUsers
            ? {
                firstName: `${formatNumber(bulkDeleteUsers.length)} users`,
                lastName: '',
                permissions: USER_PERMISSIONS.VIEWER,
              }
            : undefined
        }
        onConfirm={confirmBulkDelete}
        onDismiss={cancelBulkDelete}
      />

      <TransferOwner
        open={!!transferOwnership}
        onConfirm={transferOwnership?.action}
        user={transferOwnership?.user}
        users={transferOwnership?.users}
        onDismiss={transferOwnership?.dismiss}
      />
    </>
  );
});

export { UserManagementPageComponent };
